// Event-based data refresh system with debouncing
// This allows any mutation to trigger UI refresh without manual reload

type RefreshCallback = () => void | Promise<void>;
type RefreshEvent = 'projects' | 'activityLogs' | 'materials' | 'labors' | 'budget' | 'categories' | 'tasks' | 'all';

class DataRefreshManager {
  private listeners: Map<RefreshEvent, Set<RefreshCallback>> = new Map();
  private pendingEvents: Set<RefreshEvent> = new Set();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTriggerTime: Map<RefreshEvent, number> = new Map();
  private readonly MIN_INTERVAL_MS = 3000; // Minimum 3s between same event triggers

  // Subscribe to refresh events
  subscribe(event: RefreshEvent, callback: RefreshCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  // Trigger refresh for specific event (debounced)
  async trigger(event: RefreshEvent): Promise<void> {
    // Rate-limit: skip if same event triggered recently
    const now = Date.now();
    const lastTime = this.lastTriggerTime.get(event) || 0;
    if (now - lastTime < this.MIN_INTERVAL_MS) {
      console.log(`[DataRefresh] Skipping ${event} (rate limited, ${now - lastTime}ms since last)`);
      return;
    }

    this.pendingEvents.add(event);
    
    // Debounce: batch multiple triggers within 100ms
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    
    this.flushTimer = setTimeout(() => this.flush(), 100);
  }

  private async flush(): Promise<void> {
    const events = new Set(this.pendingEvents);
    this.pendingEvents.clear();
    this.flushTimer = null;

    const now = Date.now();
    
    // Determine which specific events to fire
    const eventsToFire = new Set<RefreshEvent>();
    
    for (const event of events) {
      if (event === 'all') {
        // 'all' expands to all specific events
        for (const key of this.listeners.keys()) {
          if (key !== 'all') eventsToFire.add(key);
        }
        eventsToFire.add('all');
      } else {
        eventsToFire.add(event);
      }
    }

    // Fire each unique event once, respecting rate limits
    for (const event of eventsToFire) {
      const lastTime = this.lastTriggerTime.get(event) || 0;
      if (now - lastTime < this.MIN_INTERVAL_MS && event !== 'all') {
        continue;
      }
      this.lastTriggerTime.set(event, now);

      const callbacks = this.listeners.get(event);
      if (callbacks) {
        console.log(`[DataRefresh] Firing ${event} (${callbacks.size} listeners)`);
        // Fire all callbacks in parallel, don't await each sequentially
        await Promise.allSettled(
          Array.from(callbacks).map(cb => Promise.resolve(cb()))
        );
      }
    }
  }

  // Trigger multiple events at once (debounced together)
  async triggerMultiple(events: RefreshEvent[]): Promise<void> {
    for (const event of events) {
      this.pendingEvents.add(event);
      this.lastTriggerTime.set(event, Date.now());
    }
    
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    this.flushTimer = setTimeout(() => this.flush(), 100);
  }
}

// Singleton instance
export const dataRefresh = new DataRefreshManager();

// Helper hook for components
import { useEffect, useCallback } from 'react';

export function useDataRefresh(
  event: RefreshEvent,
  callback: RefreshCallback,
  deps: any[] = []
) {
  const memoizedCallback = useCallback(callback, deps);

  useEffect(() => {
    const unsubscribe = dataRefresh.subscribe(event, memoizedCallback);
    return unsubscribe;
  }, [event, memoizedCallback]);
}
